import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { Cadence } from '@/enterprise/features/offerModal/types';
import { checkIfDurationIsATypicalOngoingOffer } from '@/enterprise/features/offerModal/utils';
import { OfferBuilderPricingStrategy } from '../../OfferBuilderContext';
import { buildTakerMarketKey } from '../../utils';
import { SubmitValue } from './useSubmitOffers';

interface ScheduleData {
  cadence: Cadence;
  endDate?: string;
  startDate?: string;
}

/**
 * creates a schedule object for offer submission
 * @param scheduleData - contains startDate, endDate, and cadence information
 * @returns schedule object or null if it's a typical ongoing offer
 */
export const createOfferSchedule = (scheduleData: ScheduleData) => {
  const isOngoingOfferWithNoSchedule = checkIfDurationIsATypicalOngoingOffer({
    cadence: scheduleData.cadence,
    endDate: scheduleData.endDate,
    startDate: scheduleData.startDate,
  });

  return isOngoingOfferWithNoSchedule
    ? null
    : {
        daysOfWeek: scheduleData.cadence.value,
        endDate: scheduleData.endDate,
        startDate: scheduleData.startDate,
      };
};

interface TakerMarketWithRate {
  takerMarket: TakerMarket;
  rate: number | undefined;
  rateType: 'APR' | 'DISC' | undefined;
  pricingStrategy?: OfferBuilderPricingStrategy | undefined;
}

interface MapSelectedToSubmitValuesArgs {
  effectiveRates: TakerMarketWithRate[];
  scheduleData?: ScheduleData;
  frequency?: 'oneTime' | 'recurring';
  instantPayEligibleKeys?: Set<string>;
}

/**
 * maps selected taker markets to submit values for offer submission
 * @param args - object containing parameters for mapping
 * @param args.effectiveRates - array of taker markets with their pre-calculated effective rates
 * @param args.scheduleData - optional schedule data for the offer
 * @param args.frequency - optional frequency for the offer (oneTime or recurring)
 * @param args.instantPayEligibleKeys - taker market keys (see buildTakerMarketKey) that should submit as instant pay offers
 * @returns array of submit values ready for submission
 */
export const mapSelectedToSubmitValues = ({
  effectiveRates,
  scheduleData,
  frequency,
  instantPayEligibleKeys,
}: MapSelectedToSubmitValuesArgs): SubmitValue[] => {
  return effectiveRates.map(({ takerMarket, rate, rateType, pricingStrategy }) => {
    // if schedule data is provided, use it, otherwise remove the schedule
    const schedule = scheduleData ? createOfferSchedule(scheduleData) : null;

    if (takerMarket.marketPricingType === 'PRICE_DISCOVERY') {
      if (instantPayEligibleKeys?.has(buildTakerMarketKey(takerMarket))) {
        return {
          submitType: 'INSTANT_PAY' as const,
          makerOrganizationUuid: takerMarket.makerOrganizationUuid,
          marketUuid: takerMarket.marketUuid,
          takerDivisionUuid: takerMarket.takerDivisionUuid,
          payload: {
            rate: rate!,
            rateType: rateType!,
            makerOrganizationUuid: takerMarket.makerOrganizationUuid,
            marketUuid: takerMarket.marketUuid,
            supplierDivisionUuid: takerMarket.takerDivisionUuid,
            referrer: { workflow: 'OFFER_BUILDER' },
          },
        };
      }

      if (pricingStrategy === 'expressAccept') {
        return {
          submitType: 'EXPRESS_ACCEPT' as const,
          makerOrganizationUuid: takerMarket.makerOrganizationUuid,
          marketUuid: takerMarket.marketUuid,
          takerDivisionUuid: takerMarket.takerDivisionUuid,
          payload: {
            rate: rate!,
            rateType: rateType!,
            makerOrganizationUuid: takerMarket.makerOrganizationUuid,
            marketUuid: takerMarket.marketUuid,
            supplierDivisionUuid: takerMarket.takerDivisionUuid,
            referrer: { workflow: 'OFFER_BUILDER' },
          },
        };
      }

      return {
        submitType: 'NAME_YOUR_RATE' as const,
        makerOrganizationUuid: takerMarket.makerOrganizationUuid,
        marketUuid: takerMarket.marketUuid,
        takerDivisionUuid: takerMarket.takerDivisionUuid,
        payload: {
          marketId: takerMarket.offerConfig.marketId,
          marketUuid: takerMarket.offerConfig.marketUuid,
          offerConfig: {
            uuid: takerMarket.offerConfig.uuid,
            isDiscountBidding: rateType === 'DISC',
            maxDiscount: rateType === 'DISC' ? rate : takerMarket.offerConfig.maxDiscount,
            maxApr: rateType === 'APR' ? rate : takerMarket.offerConfig.maxApr,
            exclusionSettings: takerMarket.offerConfig.exclusionSettings,
            expireOn: null, // set expireOn to null in favor of schedule
            schedule,
            frequency: frequency === 'oneTime' ? 'ONE_TIME' : 'RECURRING',
          },
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
          takerId: takerMarket.offerConfig.divisionId,
          referrer: { workflow: 'OFFER_BUILDER' },
        },
      };
    } else if (takerMarket.marketPricingType === 'BENCHMARK') {
      return {
        submitType: 'BENCHMARK' as const,
        makerOrganizationUuid: takerMarket.makerOrganizationUuid,
        marketUuid: takerMarket.marketUuid,
        takerDivisionUuid: takerMarket.takerDivisionUuid,
        payload: {
          marketUuid: takerMarket.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
          expireOn: null, // set expireOn to null in favor of schedule
          schedule,
          frequency: frequency === 'oneTime' ? 'one-time' : 'recurring',
          referrer: { workflow: 'OFFER_BUILDER' },
        },
      };
    } else {
      return {
        submitType: 'FIXED' as const,
        makerOrganizationUuid: takerMarket.makerOrganizationUuid,
        marketUuid: takerMarket.marketUuid,
        takerDivisionUuid: takerMarket.takerDivisionUuid,
        payload: {
          rate: rate!,
          marketUuid: takerMarket.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
          expireOn: null, // set expireOn to null in favor of schedule
          schedule,
          frequency: frequency === 'oneTime' ? 'one-time' : 'recurring',
          referrer: { workflow: 'OFFER_BUILDER' },
        },
      };
    }
  });
};
